import { Link, useOutletContext, useParams } from 'react-router';
import { useAgent, useCatalog, useRevealSensitive } from '../api/hooks';
import { openAuthenticatedFile } from '../api/client';
import { AssignDialog } from '../components/assign-dialog';
import { ErrorNote, Loading, PageHead, StatusBadge, formatDate } from '../components/ui';

interface MeContext {
  roles: Array<{ role: string }>;
}

export function AgentDetailPage() {
  const { id = '' } = useParams();
  const me = useOutletContext<MeContext>();
  const agent = useAgent(id);
  const statuses = useCatalog('agent-statuses');
  const eventTypes = useCatalog('career-event-types');
  const reveal = useRevealSensitive();
  const isDrh = me.roles.some((r) => r.role === 'drh');

  if (agent.isPending) return <Loading what="Ouverture du dossier" />;
  if (agent.error) return <ErrorNote error={agent.error} />;
  const a = agent.data;
  const status = statuses.data?.find((s) => s.id === a.statusId)?.label ?? '—';
  const eventLabel = (typeId: string) => eventTypes.data?.find((t) => t.id === typeId)?.label ?? typeId;

  return (
    <>
      <PageHead
        eyebrow={`Matricule ${a.matricule}`}
        title={`${a.lastName.toUpperCase()} ${a.firstName}`}
        actions={
          <>
            {isDrh && (
              <Link className="btn btn-sm" to={`/agents/${id}/edit`}>
                Modifier
              </Link>
            )}
            {isDrh && <AssignDialog agentId={id} signedDecrees={a.decrees.filter((d) => d.status === 'signed')} />}
            <Link to="/agents">← Tous les agents</Link>
          </>
        }
      >
        {status}
        {a.keycloakUserId ? '' : ' · aucun compte de connexion relié'}
      </PageHead>

      <div className="grid-2">
        <section className="card">
          <h2>Identité</h2>
          <dl className="kv" style={{ marginTop: 10 }}>
            <dt>Naissance</dt>
            <dd>{formatDate(a.birthDate)}</dd>
            <dt>Courriel</dt>
            <dd>{a.email ?? '—'}</dd>
            <dt>Téléphone</dt>
            <dd>{a.phone ?? '—'}</dd>
            <dt>NIR</dt>
            <dd className="mono">{reveal.data?.nir ?? a.nirMasked ?? '—'}</dd>
            <dt>IBAN</dt>
            <dd className="mono">{reveal.data?.iban ?? a.ibanMasked ?? '—'}</dd>
          </dl>
          {isDrh && !reveal.data && (
            <div className="actions" style={{ marginTop: 12 }}>
              <button className="btn btn-sm" disabled={reveal.isPending} onClick={() => reveal.mutate(id)}>
                Afficher NIR et IBAN
              </button>
              <span className="muted small">Consultation inscrite au journal d'audit.</span>
            </div>
          )}
          <ErrorNote error={reveal.error} />
        </section>

        <section className="card">
          <h2>Affectation en cours</h2>
          {a.currentAssignments.length === 0 ? (
            <p className="muted">Aucune affectation en cours.</p>
          ) : (
            a.currentAssignments.map((c) => (
              <dl className="kv" style={{ marginTop: 10 }} key={c.assignment.id}>
                <dt>Poste</dt>
                <dd>
                  {c.position.title} <span className="mono muted">{c.position.code}</span>
                </dd>
                <dt>Service</dt>
                <dd>
                  {c.service.name} <span className="mono muted">{c.service.code}</span>
                </dd>
                <dt>Depuis le</dt>
                <dd>{formatDate(c.assignment.startsOn)}</dd>
                <dt>Quotité</dt>
                <dd>{c.assignment.quotity} %</dd>
              </dl>
            ))
          )}
        </section>
      </div>

      <div className="grid-2" style={{ marginTop: 16 }}>
        <section className="card">
          <h2>Carrière</h2>
          {a.careerEvents.length === 0 ? (
            <p className="muted">Aucun événement enregistré.</p>
          ) : (
            <div className="tw" style={{ marginTop: 10 }}>
              <table>
                <thead>
                  <tr>
                    <th>Effet</th>
                    <th>Événement</th>
                  </tr>
                </thead>
                <tbody>
                  {a.careerEvents.map((e) => (
                    <tr key={e.id}>
                      <td>{formatDate(e.effectiveOn)}</td>
                      <td>{eventLabel(e.typeId)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card">
          <h2>Arrêtés</h2>
          {a.decrees.length === 0 ? (
            <p className="muted">Aucun arrêté.</p>
          ) : (
            <div className="tw" style={{ marginTop: 10 }}>
              <table>
                <thead>
                  <tr>
                    <th>Numéro</th>
                    <th>Objet</th>
                    <th>État</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {a.decrees.map((d) => (
                    <tr key={d.id}>
                      <td className="mono">{d.number ?? '—'}</td>
                      <td>{d.subject}</td>
                      <td>
                        <StatusBadge status={d.status} />
                      </td>
                      <td>
                        {d.status === 'signed' && (
                          <button className="btn btn-sm" onClick={() => void openAuthenticatedFile(`/api/v1/decrees/${d.id}/pdf`)}>
                            PDF
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
